import React, { useEffect, useState } from 'react';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface CompactReportingControlsProps {
  range: number | null;
  setRange: (range: number | null) => void;
  setStartDate: (date: string | null) => void;
  setEndDate: (date: string | null) => void;
  activeTab: string;
  loading: boolean;
  handleDownloadReport: (type: string) => void;
}

const CompactReportingControls: React.FC<CompactReportingControlsProps> = ({
  range,
  setRange,
  setStartDate,
  setEndDate,
  activeTab,
  loading,
  handleDownloadReport,
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [state, setState] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    },
  ]);

  // Format date as YYYY-MM-DD
  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toggleDatePicker = (): void => {
    setShowDatePicker(!showDatePicker);
  };

  const handleDateSelection = (ranges: any): void => {
    const { selection } = ranges;
    setState([selection]);
  };

  const applyDateRange = (): void => {
    const startDate = new Date(state[0].startDate);
    const endDate = new Date(state[0].endDate);

    setRange(null);
    setStartDate(formatDateForAPI(startDate));
    setEndDate(formatDateForAPI(endDate));
    setShowDatePicker(false);
  };

  useEffect(() => {
    function handleClick(e: MouseEvent): void {
      const target = e.target as HTMLElement;

      if (
        !target.classList.contains('custom-date-btn') &&
        target.closest('.date-picker-box') === null
      ) {
        if (showDatePicker) setShowDatePicker(false);
      }
    }
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDatePicker]);

  const handleRange = (rangeValue: number): void => {
    setRange(rangeValue);
    setStartDate(null);
    setEndDate(null);
    setShowDatePicker(false);
  };

  const getDownloadButtonText = (): string => {
    switch (activeTab) {
      case 'responses':
        return 'Download Responses';
      case 'conversations':
        return 'Download Conversations';
      case 'feedback':
        return 'Download Feedback';
      default:
        return 'Download Report';
    }
  };

  const getDownloadType = (): string => {
    switch (activeTab) {
      case 'responses':
        return 'responses_download';
      case 'conversations':
        return 'conversations_download';
      case 'feedback':
        return 'feedback_download';
      default:
        return 'responses_download';
    }
  };

  return (
    <div
      style={{
        background: '#f9fafb',
        padding: '12px 20px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #e5e7eb',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            position: 'relative',
          }}
        >
          <span
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#6b7280',
              marginRight: '4px',
            }}
          >
            📅
          </span>
          <div
            style={{
              display: 'inline-flex',
              background: '#fff',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              overflow: 'hidden',
            }}
          >
            <button
              onClick={() => handleRange(30)}
              style={{
                padding: '8px 16px',
                border: 'none',
                background: range === 30 ? '#3b82f6' : 'transparent',
                color: range === 30 ? '#fff' : '#374151',
                fontSize: '14px',
                fontWeight: range === 30 ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRight: '1px solid #d1d5db',
              }}
            >
              30d
            </button>
            <button
              onClick={() => handleRange(60)}
              style={{
                padding: '8px 16px',
                border: 'none',
                background: range === 60 ? '#3b82f6' : 'transparent',
                color: range === 60 ? '#fff' : '#374151',
                fontSize: '14px',
                fontWeight: range === 60 ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRight: '1px solid #d1d5db',
              }}
            >
              60d
            </button>
            <button
              onClick={() => handleRange(90)}
              style={{
                padding: '8px 16px',
                border: 'none',
                background: range === 90 ? '#3b82f6' : 'transparent',
                color: range === 90 ? '#fff' : '#374151',
                fontSize: '14px',
                fontWeight: range === 90 ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRight: '1px solid #d1d5db',
              }}
            >
              90d
            </button>
            <button
              onClick={toggleDatePicker}
              className="custom-date-btn"
              style={{
                padding: '8px 16px',
                border: 'none',
                background:
                  range !== 30 && range !== 60 && range !== 90
                    ? '#3b82f6'
                    : 'transparent',
                color:
                  range !== 30 && range !== 60 && range !== 90
                    ? '#fff'
                    : '#374151',
                fontSize: '14px',
                fontWeight:
                  range !== 30 && range !== 60 && range !== 90 ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Custom
            </button>
          </div>

          {showDatePicker && (
            <div
              className="date-picker-box"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '8px',
                zIndex: 1000,
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                borderRadius: '8px',
                overflow: 'hidden',
                background: '#fff',
              }}
            >
              <DateRange
                editableDateInputs={true}
                onChange={handleDateSelection}
                moveRangeOnFirstSelection={false}
                ranges={state}
                months={1}
                rangeColors={['#3b82f6']}
                maxDate={new Date()}
              />
              <div
                style={{
                  padding: '12px',
                  borderTop: '1px solid #e5e7eb',
                  background: '#f9fafb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  Tip: Select start date only, end defaults to today
                </span>
                <button
                  onClick={applyDateRange}
                  style={{
                    padding: '6px 16px',
                    background: '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => handleDownloadReport(getDownloadType())}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            background: loading ? '#9ca3af' : '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            boxShadow: loading ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
          onMouseOver={e => {
            if (!loading) {
              e.currentTarget.style.background = '#2563eb';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
            }
          }}
          onMouseOut={e => {
            if (!loading) {
              e.currentTarget.style.background = '#3b82f6';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
            }
          }}
        >
          <span>📥</span>
          <span>{loading ? 'Downloading...' : getDownloadButtonText()}</span>
        </button>
      </div>
    </div>
  );
};

export default CompactReportingControls;

